import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useContext,
  useMemo,
} from "react";

import { useSearchParams } from "react-router-dom";
import { isSameDay, parseISO, format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import openSocket from "../../services/socket-io";
import { AlertCircle, ArrowLeftRight, Ban, Check, CheckCheck, CheckSquare, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Clock, Download, Eye, FileArchive, FileSpreadsheet, FileText, Forward, History, Loader2, Lock, RotateCw, Search, SlidersHorizontal, Smartphone, Star, StickyNote, X } from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { cn } from "../../lib/utils";

import MarkdownWrapper from "../MarkdownWrapper";
import TemplateMessage from "../TemplateMessage";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import VisualizadorDeArquivo from "../VisualizadorDeArquivo";
import MiniaturaDePdf from "../MiniaturaDePdf";
import { extensaoDe } from "../../helpers/tiposDeArquivo";
import { rotuloDeMidia, temMiniatura } from "../../helpers/rotuloDeMidia";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import usePrefTickets from "../../hooks/usePrefTickets";
import useAtalhoDeTeclado from "../../hooks/useAtalhoDeTeclado";
import usePermissions from "../../hooks/usePermissions";
import ForwardMessageModal from "../ForwardMessageModal";
import { IDS_BOTOES_DA_CONVERSA } from "../Ticket/botoesDaConversa";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import { textoDeErro } from "../../errors/textoDeErro";
import { mapaDeMencoes, trocarMencoes } from "../../helpers/mencoes";

// Mensagem que não saiu. O backend grava a falha com este ack
// (backend/src/services/WbotServices/RegistrarFalhaDeEnvio.ts) para a bolha
// existir na conversa em vez de a mensagem sumir junto com o toast.
const ACK_FALHA = -1;

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
      return [...state];
    }

    // Entra pela data, não no fim da lista. O socket não garante ordem de
    // chegada (mídia baixada demora mais que texto, e o eco do WhatsApp pode
    // vir depois do que já foi gravado), e empurrar sempre para o fim deixava
    // a conversa fora de ordem até dar F5.
    const nova = [...state, newMessage];
    nova.sort((a, b) => {
      const diff = new Date(a.createdAt) - new Date(b.createdAt);
      return diff !== 0 ? diff : String(a.id).localeCompare(String(b.id));
    });

    return nova;
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  // Desfavoritar com o filtro "só favoritas" ligado tira a mensagem da lista
  // sem precisar recarregar a conversa inteira.
  if (action.type === "REMOVE_MESSAGE") {
    return state.filter((m) => m.id !== action.payload);
  }

  // As reações moram na própria mensagem, e não num mapa à parte, porque é
  // assim que elas são desenhadas: um chip embaixo daquele balão. O payload
  // aceita as duas formas em que elas chegam — o mapa inteiro que vem junto
  // com a página de mensagens (`mapa`) e a atualização de uma só, que vem pelo
  // socket ou da resposta do POST.
  if (action.type === "SET_REACTION") {
    const mapa = action.payload.mapa || {
      [action.payload.messageId]: action.payload.reacoes,
    };

    return state.map((m) =>
      mapa[m.id] !== undefined ? { ...m, reacoes: mapa[m.id] } : m,
    );
  }

  if (action.type === "RESET") {
    return [];
  }
};

// Quantas páginas a mais o sistema aceita carregar sozinho atrás de um
// resultado de busca antigo. Sem teto, escolher a primeira mensagem de uma
// conversa de dois anos puxaria a conversa inteira, de 20 em 20.
const MAX_PAGINAS_ATRAS_DO_ALVO = 25;

// "normal" é exatamente o tamanho de antes: quem não mexer não vê diferença.
const TAMANHOS_DA_FONTE = {
  compacto: "14px",
  normal: "16px",
  grande: "18px",
};

// A janela em que o WhatsApp ainda aceita a edição: 15 minutos depois de
// enviada. Depois disso o provider recusa, e oferecer o botão só levaria a um
// erro — a mensagem já está no aparelho de quem recebeu para sempre.
//
// Mídia fica de fora: nenhum provider troca o arquivo já enviado.
const MINUTOS_DE_EDICAO = 15;

const aindaDaParaEditar = (message) =>
  Boolean(
    message.fromMe &&
      !message.isDeleted &&
      !message.mediaUrl &&
      Date.now() - new Date(message.createdAt).getTime() <
        MINUTOS_DE_EDICAO * 60 * 1000,
  );

const MessagesList = ({
  ticketId,
  isGroup,
  // Do grupo: trocam o `@<digitos>` das menções pelo nome.
  participantes,
  podeReagir = true,
  // Canal que não edita mensagem enviada (Meta, sidecar de voz) não mostra o
  // item "Editar" — o botão só levaria a um 422.
  podeEditar = true,
  // Canal que não manda recibo de entregue/lido (o sidecar de voz do
  // whatsmeow, o Telegram) não desenha tique nenhum: a mensagem ficava com o
  // relógio de "aguardando" para sempre, dizendo que não saiu quando saiu.
  mostrarRecibos = true,
  reserva = null,
}) => {
  const [tamanhoFonteChat] = usePrefTickets("tamanhoFonteChat", "normal");
  const [texturaDoChat] = usePrefTickets("texturaDoChat", true);
  const [botoesDaConversa] = usePrefTickets(
    "botoesDaConversa",
    IDS_BOTOES_DA_CONVERSA,
  );
  const temBotao = (id) => botoesDaConversa.includes(id);
  // Transcrever gasta crédito de IA e por isso o backend exige `ai:use`. Lido
  // UMA vez aqui e descendo por prop: a lista redesenha centenas de balões, e
  // um hook por balão seria um `useEffect` por mensagem para responder sempre
  // a mesma coisa.
  const { temPermissao } = usePermissions();
  const podeTranscrever = temPermissao("ai:use");
  const mencoes = useMemo(() => mapaDeMencoes(participantes), [participantes]);
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  // Nasce carregando: o efeito de busca só roda depois do primeiro paint, e
  // com `false` o estado de conversa nova piscava antes dele.
  const [loading, setLoading] = useState(true);
  const lastMessageRef = useRef();
  // Conversa longa: quem sobe para reler perde o fim de vista, e não há
  // rolagem automática enquanto está lá em cima. O botão traz de volta.
  const [foraDoFim, setForaDoFim] = useState(false);

  // Histórico do contato, carregado sob demanda. O atendimento é fechado e o
  // cliente volta dias depois: o ticket novo nasce vazio, e sem isto quem
  // atende não vê nada do que já foi dito. As mensagens antigas entram na
  // MESMA lista (carregam por `ticketId` próprio e por isso trazem o selo de
  // qual atendimento são), de trás para frente: página a página de um
  // atendimento, e só então o anterior a ele.
  const [historico, setHistorico] = useState({
    tickets: null,
    idx: 0,
    pagina: 1,
  });
  const [carregandoAnterior, setCarregandoAnterior] = useState(false);
  // Contato e data deste atendimento: vêm do próprio `/messages`, que já
  // devolve o ticket. Sem isso seria mais uma prop atravessando a tela.
  const [dadosDoTicket, setDadosDoTicket] = useState({
    contactId: null,
    createdAt: null,
  });

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  // Filtros da conversa. Ficam aqui, e não no pai, porque quem pagina e
  // acumula as mensagens é este componente — mudar o filtro tem que zerar a
  // lista e voltar para a página 1, senão as mensagens do filtro antigo ficam
  // misturadas com as do novo.
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [apenasFavoritas, setApenasFavoritas] = useState(false);
  // Falha da busca não é conversa vazia: sem este estado, um erro de rede
  // mostrava "Comece a conversa com este contato." numa conversa cheia.
  const [erroAoCarregar, setErroAoCarregar] = useState(false);
  // Atendimento aguardando que este cargo não pode ler antes de aceitar. Não
  // é falha de carregamento: é regra, e a tela precisa dizer isso em vez de
  // oferecer "tentar de novo" para sempre.
  const [bloqueadoAteAceitar, setBloqueadoAteAceitar] = useState(false);
  // Muda a cada "Tentar de novo" só para o efeito de busca rodar outra vez.
  const [tentativa, setTentativa] = useState(0);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  // Busca de texto dentro da conversa. Fica separada dos filtros acima porque
  // ela NÃO recorta a lista: a conversa continua inteira e a busca só aponta
  // para onde ir — como no WhatsApp.
  const [termoBusca, setTermoBusca] = useState("");
  const [achados, setAchados] = useState([]);
  const [indiceAchado, setIndiceAchado] = useState(0);
  const [buscando, setBuscando] = useState(false);
  // Id da mensagem que a busca quer alcançar e id da que está piscando.
  const [alvoBusca, setAlvoBusca] = useState(null);
  const [destacada, setDestacada] = useState(null);
  const buscaRef = useRef(null);
  // Por mensagem, não um só booleano: com duas falhas na conversa, um estado
  // global deixaria os dois botões girando por causa de um clique.
  const [reenviando, setReenviando] = useState({});
  const paginasPuxadasRef = useRef(0);

  // Ids favoritados POR ESTE atendente. Vem do backend a cada página; o Set
  // acumula porque a conversa é carregada aos poucos.
  const [favoritos, setFavoritos] = useState(() => new Set());

  // Escolher arquivos na própria conversa. Ligado o modo, a bolha deixa de
  // abrir o arquivo e passa a marcar/desmarcar — só as que TÊM anexo; clicar
  // numa mensagem de texto não faz nada, para não dar a impressão de que ela
  // vai no ZIP.
  const [modoSelecao, setModoSelecao] = useState(false);
  const [encaminhandoSelecao, setEncaminhandoSelecao] = useState(false);
  const [selecionadas, setSelecionadas] = useState(() => new Set());
  const [baixando, setBaixando] = useState(null);

  // Arquivo aberto no visualizador (índice dentro de `arquivosDaConversa`).
  const [visualizando, setVisualizando] = useState(null);

  // Citar a mensagem no campo de resposta — o mesmo estado que o menu da bolha
  // usa; aqui ele serve ao atalho de dois cliques.
  const { setReplyingMessage } = useContext(ReplyMessageContext);

  const filtrando = Boolean(dataInicio || dataFim || apenasFavoritas);

  // Tem mensagem de outro atendimento na lista? Então os blocos precisam de
  // selo. Calculado uma vez por pintura: dentro do separador seria uma
  // varredura por balão.
  const misturaAtendimentos = messagesList.some(
    (m) => String(m.ticketId) !== String(ticketId),
  );

  // Ref, e não dependência do efeito do socket: colocar `filtrando` nas deps
  // faria o Socket.IO desconectar e reconectar a cada tecla no campo de data.
  const filtrandoRef = useRef(false);
  filtrandoRef.current = filtrando;

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setFavoritos(new Set());
    setDataInicio("");
    setDataFim("");
    setApenasFavoritas(false);
    limparBusca();
    setHistorico({ tickets: null, idx: 0, pagina: 1 });
    setDadosDoTicket({ contactId: null, createdAt: null });

    currentTicketId.current = ticketId;
  }, [ticketId]);

  // Vindo de um resultado de busca (lista ou Ctrl+K), a URL traz a mensagem
  // achada e o termo: a conversa abre NELA, com a busca preenchida para andar
  // pelas outras ocorrências. Os parâmetros saem da URL logo em seguida — um
  // F5 depois não deve pular de volta para o resultado antigo.
  const [parametrosDaUrl, setParametrosDaUrl] = useSearchParams();
  const alvoDaUrlRef = useRef(null);
  // Enquanto o salto vindo da URL não acontece, a carga inicial não pode
  // rolar para o fim: ela chega depois e atropelava o salto (F5 ou link).
  const segurarFimRef = useRef(false);

  useEffect(() => {
    const busca = parametrosDaUrl.get("busca");
    const mensagem = parametrosDaUrl.get("mensagem");
    if (!busca && !mensagem) return;

    alvoDaUrlRef.current = mensagem;
    segurarFimRef.current = Boolean(mensagem);
    paginasPuxadasRef.current = 0;
    if (busca) setTermoBusca(busca);
    else setAlvoBusca(mensagem);

    setParametrosDaUrl(
      (anteriores) => {
        anteriores.delete("busca");
        anteriores.delete("mensagem");
        return anteriores;
      },
      { replace: true },
    );
  }, [ticketId, parametrosDaUrl, setParametrosDaUrl]);

  // Trocar o filtro descarta o que já estava carregado: a lista acumulada é de
  // outro recorte.
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [dataInicio, dataFim, apenasFavoritas]);

  // Sem espera artificial: quem abre conversa clicou uma vez, não digitou.
  // Os 500 ms que havia aqui eram meio segundo somado ao meio segundo do
  // cabeçalho (components/Ticket) em toda abertura.
  //
  // A resposta que chega fora de hora é descartada por inteiro: `atual` cobre
  // o efeito todo — o `currentTicketId` sozinho deixava passar o rolar até o
  // fim, o fim do carregando e o toast de erro, que são justamente o que a
  // conversa anterior faria na tela da nova.
  useEffect(() => {
    let atual = true;

    setLoading(true);
    setErroAoCarregar(false);

    const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: {
              pageNumber,
              dataInicio: dataInicio || undefined,
              dataFim: dataFim || undefined,
              apenasFavoritas: apenasFavoritas ? "true" : undefined,
            },
          });

          if (atual && currentTicketId.current === ticketId) {
            if (data.ticket) {
              setDadosDoTicket({
                contactId: data.ticket.contactId,
                createdAt: data.ticket.createdAt,
              });
            }
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            // Depois do LOAD: as reações só têm onde pousar quando as
            // mensagens da página já estão na lista.
            if (data.reactions) {
              dispatch({
                type: "SET_REACTION",
                payload: { mapa: data.reactions },
              });
            }
            setHasMore(data.hasMore);
            setLoading(false);

            if (data.favoritedIds?.length) {
              setFavoritos((anterior) => {
                const novo = new Set(anterior);
                data.favoritedIds.forEach((id) => novo.add(id));
                return novo;
              });
            }
          }

          if (atual && pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          if (!atual) return;

          setLoading(false);

          const semEspiada =
            err?.response?.data?.error === "ERR_PEEK_PENDING_NOT_ALLOWED";

          if (currentTicketId.current === ticketId) {
            setBloqueadoAteAceitar(semEspiada);
            setErroAoCarregar(!semEspiada);
          }

          // Regra conhecida não é erro para quem usa: o toast vermelho dizia
          // "ERR_PEEK_PENDING_NOT_ALLOWED" a cada clique na lista.
          if (!semEspiada) toastError(err);
        }
    };

    fetchMessages();

    return () => {
      atual = false;
    };
  }, [pageNumber, ticketId, dataInicio, dataFim, apenasFavoritas, tentativa]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    // Mensagem que chegou enquanto a conexão estava caída não volta sozinha:
    // o histórico precisa ser buscado de novo.
    socket.on("resync", () => setTentativa((n) => n + 1));

    socket.on("appMessage", (data) => {
      // Só o que é DESTE atendimento.
      //
      // A conexão do Socket.IO é uma só para o app inteiro, e a lista de
      // tickets e as notificações entram nas salas "open"/"pending"/
      // "notification" — para onde o backend também manda o appMessage de
      // cada mensagem nova. Sem esta conferência, a conversa aberta recebia
      // mensagem de TODOS os atendimentos da empresa e as misturava no fim
      // da lista.
      if (Number(data.message?.ticketId) !== Number(ticketId)) return;

      if (data.action === "create") {
        // Com filtro ligado a lista é um recorte (um período, ou só as
        // favoritas). Empurrar a mensagem nova ali dentro furaria o recorte:
        // ela apareceria fora do período escolhido, sem estar favoritada.
        if (filtrandoRef.current) return;

        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }

      // Reenvio que deu certo: a linha da falha some e a mensagem enviada
      // chega como "create", com o id do provider. Sem tratar aqui, quem não
      // clicou continuaria vendo a bolha de falha até um F5.
      if (data.action === "delete") {
        dispatch({ type: "REMOVE_MESSAGE", payload: data.message.id });
      }
    });

    // A reação é emitida na sala da empresa, então chega a reação de qualquer
    // atendimento — a mesma peneira do appMessage vale aqui.
    socket.on("appMessageReaction", (data) => {
      if (Number(data.ticketId) !== Number(ticketId)) return;

      dispatch({
        type: "SET_REACTION",
        payload: { messageId: data.messageId, reacoes: data.reacoes },
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  // Busca dentro da conversa. Debounce para não disparar uma requisição por
  // tecla, e mínimo de 2 caracteres porque o backend recusa menos que isso.
  useEffect(() => {
    const termo = termoBusca.trim();

    if (termo.length < 2) {
      setAchados([]);
      setIndiceAchado(0);
      setBuscando(false);
      return undefined;
    }

    setBuscando(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/messages/${ticketId}/search`, {
          params: { q: termo },
        });

        if (currentTicketId.current !== ticketId) return;

        const encontrados = data.achados || [];
        // A mensagem que a lista apontou manda; sem ela, o primeiro achado.
        const alvoDaUrl = alvoDaUrlRef.current;
        alvoDaUrlRef.current = null;
        const naLista = encontrados.findIndex(
          (m) => String(m.id) === String(alvoDaUrl),
        );
        const indice = Math.max(0, naLista);

        setAchados(encontrados);
        setIndiceAchado(indice);

        // Vai direto para o achado. Sem isto, com um resultado só não havia
        // como alcançá-lo: as setas só aparecem quando há mais de um.
        if (encontrados.length || alvoDaUrl) {
          paginasPuxadasRef.current = 0;
          // Fora dos achados (a busca da conversa tem teto), vai mesmo assim.
          setAlvoBusca(
            alvoDaUrl && naLista < 0 ? alvoDaUrl : encontrados[indice].id,
          );
        }
      } catch (err) {
        setAchados([]);
        segurarFimRef.current = false;
        toastError(err);
      }
      setBuscando(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [termoBusca, ticketId]);

  // Levar a conversa até a mensagem escolhida.
  //
  // Limitação assumida: a mensagem pode estar numa página que ainda não foi
  // carregada. Quando ela não está no DOM, o efeito pede mais uma página e se
  // repete quando a lista muda — ou seja, ele "rola para trás" sozinho até
  // achar. Isso tem custo (uma requisição por página, de 20 em 20), por isso o
  // teto do MAX_PAGINAS_ATRAS_DO_ALVO: passando disso a busca desiste e avisa,
  // em vez de puxar a conversa inteira em silêncio.
  useEffect(() => {
    if (!alvoBusca) return undefined;

    const elemento = document.getElementById(`mensagem-${alvoBusca}`);

    if (elemento) {
      // Bolha maior que a tela (documento colado): centralizar a deixava
      // inteira fora de vista. Nesse caso alinha pelo começo.
      elemento.scrollIntoView({
        block:
          elemento.offsetHeight > window.innerHeight * 0.6 ? "start" : "center",
        // Vindo da URL o salto é seco: a rolagem suave era interrompida pela
        // página anterior carregando por cima e parava no meio do caminho.
        behavior: segurarFimRef.current ? "auto" : "smooth",
      });
      setDestacada(alvoBusca);
      setAlvoBusca(null);
      paginasPuxadasRef.current = 0;
      // Depois de chegar, o fim volta a valer (mensagem nova rola a tela).
      // Espera a carga que já estava na fila passar sem mexer na rolagem.
      setTimeout(() => {
        segurarFimRef.current = false;
      }, 1500);

      const timer = setTimeout(() => setDestacada(null), 3000);
      return () => clearTimeout(timer);
    }

    if (loading) return undefined;

    if (!hasMore || paginasPuxadasRef.current >= MAX_PAGINAS_ATRAS_DO_ALVO) {
      toast.info(
        "Essa mensagem é antiga demais para ser aberta pela busca. Role a conversa para cima para carregá-la.",
      );
      setAlvoBusca(null);
      paginasPuxadasRef.current = 0;
      segurarFimRef.current = false;
      return undefined;
    }

    paginasPuxadasRef.current += 1;
    loadMore();

    return undefined;
    // `loadMore` fora das deps de propósito: ela é recriada a cada render e
    // entrar aqui refaria este efeito a cada pintura da conversa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alvoBusca, messagesList, hasMore, loading]);

  const irParaAchado = (indice) => {
    const achado = achados[indice];
    if (!achado) return;

    setIndiceAchado(indice);
    paginasPuxadasRef.current = 0;
    setAlvoBusca(achado.id);
  };

  const limparBusca = () => {
    segurarFimRef.current = false;
    setTermoBusca("");
    setAchados([]);
    setIndiceAchado(0);
    setAlvoBusca(null);
    setDestacada(null);
  };

  // Alt+f: o "/" já é da busca da lista de atendimentos, este é só da
  // conversa aberta. O hook já recusa enquanto se digita e com modal aberto.
  useAtalhoDeTeclado("f", () => buscaRef.current?.focus(), true, {
    alt: true,
  });

  // Enter/Shift+Enter andam pelos resultados sem soltar o campo; Esc limpa a
  // busca e devolve o foco para quem escreve — sair da busca não deve deixar
  // o foco preso ali nem cair no body.
  const aoTeclarBusca = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      limparBusca();
      document.dispatchEvent(new CustomEvent("composer:focar"));
      return;
    }

    if (e.key !== "Enter" || !achados.length) return;
    e.preventDefault();

    irParaAchado(
      e.shiftKey
        ? (indiceAchado - 1 + achados.length) % achados.length
        : (indiceAchado + 1) % achados.length,
    );
  };

  const reagir = async (message, emoji) => {
    // Devolve o foco já no gesto do clique, não depois do await: com rede
    // lenta, esperar a resposta podia roubar o foco de outro campo onde a
    // pessoa já tivesse clicado enquanto isso (por exemplo a busca), e no
    // caminho de erro o foco nunca voltava. Disparando aqui cobre os dois —
    // sucesso e falha — igual.
    document.dispatchEvent(new CustomEvent("composer:focar"));

    try {
      const { data } = await api.post(`/messages/${message.id}/reaction`, {
        emoji,
      });

      dispatch({
        type: "SET_REACTION",
        payload: { messageId: data.messageId, reacoes: data.reacoes },
      });
    } catch (err) {
      toastError(err);
    }
  };

  // Altura da lista no instante em que se pediu a página anterior. As
  // mensagens antigas entram ACIMA das que estão na tela: sem devolver a
  // rolagem, o navegador mantém o mesmo scrollTop e a conversa dá um salto —
  // quem rolava para ler o passado era jogado para outro trecho, e como o topo
  // continuava a menos de 50px o próximo gesto puxava mais uma página, e mais
  // outra.
  const alturaAntesDaPagina = useRef(null);

  // Marca a altura de agora para o `useLayoutEffect` abaixo devolver a rolagem
  // quando a lista crescer POR CIMA. Uma função só: paginar para trás e
  // carregar o atendimento anterior fazem a mesma coisa com a conversa, e a
  // regra de imutabilidade do lint exige que a escrita esteja declarada antes
  // do efeito que a lê.
  const marcarAlturaAtual = () => {
    const lista = document.getElementById("messagesList");
    alturaAntesDaPagina.current = lista ? lista.scrollHeight : null;
  };

  // Desistiu: a marca não pode ficar para trás, senão a próxima mudança da
  // lista devolve a rolagem para um lugar que não existe mais.
  const descartarAltura = () => {
    alturaAntesDaPagina.current = null;
  };

  const loadMore = () => {
    marcarAlturaAtual();
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  useLayoutEffect(() => {
    const anterior = alturaAntesDaPagina.current;
    if (anterior === null) return;

    const lista = document.getElementById("messagesList");
    if (!lista) return;

    const crescimento = lista.scrollHeight - anterior;
    alturaAntesDaPagina.current = null;

    if (crescimento > 0) lista.scrollTop += crescimento;
  }, [messagesList]);

  // Rolar no mesmo tique do dispatch não funciona: a bolha nova ainda não
  // está no DOM e a conversa parava uma mensagem atrás a cada envio. Dois
  // quadros à frente o React já pintou a lista, e aí o fim é o fim de verdade.
  const scrollToBottom = (behavior) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const lista = document.getElementById("messagesList");
        if (!lista || segurarFimRef.current) return;
        lista.scrollTo({ top: lista.scrollHeight, behavior });
        setForaDoFim(false);
      });
    });
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    setForaDoFim(el.scrollHeight - el.scrollTop - el.clientHeight > 200);

    if (!hasMore) return;
    const { scrollTop } = el;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  // Dois cliques na bolha respondem aquela mensagem.
  //
  // Responder citando já existia, mas escondido atrás da setinha que só aparece
  // ao passar o mouse: eram três passos (achar a seta, abrir o menu, escolher
  // "Responder") para o gesto mais comum de uma conversa. O duplo clique é o
  // mesmo atalho do WhatsApp Web.
  const responderMensagem = (message) => {
    if (modoSelecao || message.isDeleted || message.isInternal) return;

    // O duplo clique seleciona a palavra sob o cursor; sem limpar, fica um
    // trecho azulado no meio da conversa sem motivo nenhum.
    window.getSelection()?.removeAllRanges();

    setReplyingMessage(message);
  };

  // Ir até a mensagem citada — reaproveita o caminho da busca, que sabe puxar
  // páginas para trás quando a mensagem ainda não foi carregada.
  const irParaMensagem = (id) => {
    if (!id) return;

    paginasPuxadasRef.current = 0;
    setAlvoBusca(id);
  };

  const [editavelAgora, setEditavelAgora] = useState(false);

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
    // O relógio é lido AQUI, no clique (a conta mora fora do componente: ler
    // a hora durante a renderização é impuro, e numa lista que renderiza a
    // cada mensagem nova daria uma resposta diferente a cada render).
    setEditavelAgora(aindaDaParaEditar(message));
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const alternarFavorito = async (message) => {
    const jaEra = favoritos.has(message.id);

    try {
      if (jaEra) {
        await api.delete(`/messages/${message.id}/favorite`);
      } else {
        await api.post(`/messages/${message.id}/favorite`);
      }

      setFavoritos((anterior) => {
        const novo = new Set(anterior);
        if (jaEra) novo.delete(message.id);
        else novo.add(message.id);
        return novo;
      });

      // Desfavoritar enquanto se olha só as favoritas: a mensagem sai da
      // lista na hora, senão fica ali contrariando o filtro.
      if (jaEra && apenasFavoritas) {
        dispatch({ type: "REMOVE_MESSAGE", payload: message.id });
      }
    } catch (err) {
      toastError(err);
    }
  };

  // Estrela de favoritar. Só aparece ao passar o mouse — a não ser quando já
  // está marcada, que é justamente o que se quer enxergar rolando a conversa.
  const renderFavoritoBotao = (message) => {
    const favorita = favoritos.has(message.id);

    return (
      <button
        type="button"
        title={favorita ? "Remover dos favoritos" : "Favoritar mensagem"}
        aria-label={favorita ? "Remover dos favoritos" : "Favoritar mensagem"}
        aria-pressed={favorita}
        onClick={() => alternarFavorito(message)}
        className={cn(
          "absolute right-7 top-0 h-7 w-7 items-center justify-center rounded-full bg-inherit text-muted-foreground hover:bg-black/5",
          favorita ? "flex text-amber-500" : "hidden group-hover:flex",
        )}
      >
        <Star className={cn("h-4 w-4", favorita && "fill-amber-400")} />
      </button>
    );
  };

  // Largura que o texto reserva para o rodapé absoluto (hora, tique e a marca
  // "editada"). Sem a folga extra na editada, a última linha de uma mensagem
  // curta fica por baixo do rodapé.
  const espacoDoRodape = (message) =>
    message.isEdited && !message.isDeleted
      ? "pr-28"
      : message.fromMe && message.origemDoEnvio
        ? "pr-20"
        : "pr-16";

  // Enviada fora da plataforma: alguém digitou no celular ou no WhatsApp Web
  // (qualquer canal de WhatsApp), ou saiu pela sessão vinculada (COEX).
  const ORIGEM_DO_ENVIO = {
    app: "Enviada pelo celular ou WhatsApp Web, fora da plataforma",
    vinculada: "Enviada pelo aparelho vinculado — sem custo da Meta",
    historico: "Enviada pelo celular antes da conexão com a plataforma",
  };
  const renderOrigemDoEnvio = (message) => {
    const texto = ORIGEM_DO_ENVIO[message.origemDoEnvio];
    if (!texto) return null;

    return (
      <span title={texto} aria-label={texto} className="inline-flex">
        <Smartphone className="h-3 w-3" />
      </span>
    );
  };

  // A marca "editada", com o texto de antes a um passar de mouse.
  //
  // Vale nos DOIS lados da conversa: quem edita mais é o cliente, e a bolha
  // dele trocava o texto em silêncio — sem a marca, quem está lendo vê a
  // frase mudar sem saber por quê, e sem o corpo anterior não sabe ao que
  // respondeu.
  const renderMarcaDeEdicao = (message) => {
    if (!message.isEdited || message.isDeleted) return null;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help underline decoration-dotted">
              editada
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs whitespace-pre-wrap break-words text-left">
            {message.previousBody ? (
              <>
                <span className="block font-medium">
                  Antes da edição
                  {message.editedAt &&
                    ` (${format(parseISO(message.editedAt), "dd/MM HH:mm")})`}
                  :
                </span>
                {message.previousBody}
              </>
            ) : (
              "Esta mensagem foi editada."
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Chips de reação, colados na base do balão. O total só aparece a partir de
  // dois: com uma pessoa só o número não informa nada e polui a conversa.
  const renderReacoes = (message) => {
    if (!message.reacoes?.length) return null;

    return (
      <div className="-mb-1 flex flex-wrap gap-1 pt-1">
        {message.reacoes.map((reacao) => (
          <button
            key={reacao.emoji}
            type="button"
            disabled={!podeReagir}
            onClick={() => reagir(message, reacao.emoji)}
            title={
              reacao.euReagi ? "Remover sua reação" : "Reagir com este emoji"
            }
            aria-pressed={reacao.euReagi}
            className={cn(
              "flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs leading-none transition-colors",
              reacao.euReagi
                ? "border-emerald-400 bg-emerald-100 dark:border-emerald-500 dark:bg-emerald-800/60"
                : "border-transparent bg-black/5 hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/20",
            )}
          >
            <span>{reacao.emoji}</span>
            {reacao.total > 1 && (
              <span className="text-[10px] text-muted-foreground">
                {reacao.total}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  };

  // Numa mensagem de mídia o corpo é a legenda. Mensagens antigas guardavam o
  // nome do arquivo ali — nesse caso não há legenda de verdade para mostrar.
  const corpoVisivel = (message) => {
    // Localização e contato JÁ são desenhados por `checkMessageMedia`
    // (`LocationPreview`/`VcardPreview`). Repetir o corpo embaixo imprimia o
    // dado cru na conversa — "https://maps.google.com/...|-15.6, -56.0" e o
    // texto do vCard — como se fosse mensagem escrita pelo cliente.
    if (message.mediaType === "location" || message.mediaType === "vcard") {
      return "";
    }

    if (!message.mediaUrl || !message.body) return message.body;

    const arquivo = decodeURIComponent(message.mediaUrl.split("/").pop() || "");

    return message.body.trim() === arquivo ? "" : message.body;
  };

  // Anexos que estão à vista na conversa, do mais antigo para o mais novo — é
  // a ordem em que a conversa aconteceu, e é por ela que as setas do
  // visualizador andam.
  const arquivosDaConversa = messagesList
    .filter((m) => m.mediaUrl && !m.isDeleted)
    .map((m) => ({
      id: m.id,
      url: m.mediaUrl,
      mediaType: m.mediaType,
      legenda: corpoVisivel(m),
      fromMe: m.fromMe,
      createdAt: m.createdAt,
    }));

  const abrirArquivo = (messageId) => {
    const indice = arquivosDaConversa.findIndex((a) => a.id === messageId);
    if (indice >= 0) setVisualizando(indice);
  };

  const alternarSelecao = (messageId) => {
    setSelecionadas((anterior) => {
      const novo = new Set(anterior);
      if (novo.has(messageId)) novo.delete(messageId);
      else novo.add(messageId);
      return novo;
    });
  };

  // Das marcadas, as que têm arquivo — é o que o ZIP aceita. Sem esta peneira,
  // marcar um texto faria o botão de baixar prometer um arquivo que não existe.
  const arquivosSelecionados = arquivosDaConversa
    .filter((a) => selecionadas.has(a.id))
    .map((a) => a.id);

  // Na ORDEM da conversa, não na ordem em que foram clicadas: quem encaminha
  // três balões espera que cheguem na sequência em que aconteceram.
  const mensagensSelecionadas = messagesList.filter((m) =>
    selecionadas.has(m.id),
  );

  const todosArquivosMarcados =
    arquivosDaConversa.length > 0 &&
    arquivosDaConversa.every((a) => selecionadas.has(a.id));

  const alternarTodosDaConversa = () => {
    setSelecionadas(() =>
      todosArquivosMarcados
        ? new Set()
        : new Set(arquivosDaConversa.map((a) => a.id)),
    );
  };

  const sairDaSelecao = () => {
    setModoSelecao(false);
    setSelecionadas(new Set());
  };

  // `ids` vazio significa "tudo o que este atendimento tem" — o backend monta
  // o ZIP a partir do banco, e não do que já foi rolado na tela. Sem isso,
  // "Baixar tudo" numa conversa longa levaria só as mensagens carregadas.
  const baixarArquivos = async (ids, rotulo) => {
    setBaixando(rotulo);
    try {
      const { data, headers } = await api.post(
        `/messages/${ticketId}/gallery/download`,
        { messageIds: ids },
        { responseType: "blob" },
      );

      const nome =
        (headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] ||
        `arquivos-atendimento-${ticketId}.zip`;

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = nome;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("O download do ZIP começou.");
      if (ids.length) sairDaSelecao();
    } catch (err) {
      // Com responseType blob a mensagem de erro também chega como blob.
      if (err?.response?.data instanceof Blob) {
        try {
          const texto = await err.response.data.text();
          err.response.data = JSON.parse(texto);
        } catch {
          // Fica o erro genérico mesmo.
        }
      }
      toastError(err);
    } finally {
      setBaixando(null);
    }
  };

  const PLANILHAS = [".xls", ".xlsx", ".csv", ".ods"];
  const COMPACTADOS = [".zip", ".rar", ".7z", ".gz", ".tgz"];

  const IconeDoDocumento = ({ extensao }) => {
    if (PLANILHAS.includes(extensao))
      return <FileSpreadsheet className="h-8 w-8 shrink-0 text-muted-foreground" />;
    if (COMPACTADOS.includes(extensao))
      return <FileArchive className="h-8 w-8 shrink-0 text-muted-foreground" />;
    return <FileText className="h-8 w-8 shrink-0 text-muted-foreground" />;
  };

  // No modo seleção, uma capa transparente cobre a bolha inteira e recebe o
  // clique. Sem ela, clicar numa imagem abriria o lightbox e num vídeo daria
  // play — o clique nunca chegaria na seleção.
  const renderCapaDeSelecao = (message) => {
    // Vale para QUALQUER mensagem, e não só para as que têm anexo: o mesmo
    // modo agora serve a baixar arquivos e a encaminhar conversa. Apagada e
    // nota interna ficam de fora — uma não existe mais para o cliente, a
    // outra nunca foi para ele.
    if (!modoSelecao || message.isDeleted || message.isInternal) return null;

    const marcada = selecionadas.has(message.id);

    return (
      <button
        type="button"
        onClick={() => alternarSelecao(message.id)}
        aria-pressed={marcada}
        aria-label={marcada ? "Desmarcar mensagem" : "Marcar mensagem"}
        className="absolute inset-0 z-10 rounded-2xl"
      >
        <span
          className={cn(
            "absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border-2 bg-background/90",
            marcada
              ? "border-primary bg-primary text-primary-foreground"
              : "border-muted-foreground/50",
          )}
        >
          {marcada && <Check className="h-3 w-3" />}
        </span>
      </button>
    );
  };

  // Classes que a bolha ganha enquanto se escolhe arquivo.
  const classesDeSelecao = (message) =>
    modoSelecao &&
    (message.isDeleted || message.isInternal
      ? // Apagada e nota interna não vão para lugar nenhum: o cinza evita a
        // dúvida de por que não marcam.
        "opacity-50"
      : cn(
          "cursor-pointer",
          selecionadas.has(message.id) && "ring-2 ring-primary ring-offset-1",
        ));

  const checkMessageMedia = (message) => {
    if (
      message.mediaType === "location" &&
      message.body.split("|").length >= 2
    ) {
      const partes = message.body.split("|");

      // Duas formas gravadas em `Messages.body`:
      //
      // - "<link do mapa>|<descrição>" — é o que TODOS os canais gravam hoje
      //   (whaileys `whaileys.ts:428`, WAHA e Telegram). Não há imagem de
      //   prévia: nenhum provider manda uma, e o `LocationPreview` desenha um
      //   alfinete no lugar.
      // - "<imagem>|<link>|<descrição>" — formato antigo, ainda no histórico.
      //
      // Ler as duas primeiras partes como imagem e link tratava o link do
      // mapa como se fosse uma imagem (que nunca carregava) e mandava a
      // DESCRIÇÃO para o botão "Visualizar" — abrir a localização não abria
      // nada. Quebrado em todo canal; só apareceu quando o Telegram entrou.
      const [imagem, link, descricao] =
        partes.length > 2
          ? [partes[0], partes[1], partes[2]]
          : [null, partes[0], partes[1]];

      return (
        <LocationPreview image={imagem} link={link} description={descricao} />
      );
    } else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />;
    } else if (message.mediaType === "image") {
      // Antes havia também uma checagem de extensão (jpe?g|png|gif). Ela era
      // redundante — o backend já deriva mediaType do mimetype — e deixava de
      // fora tudo o mais que o WhatsApp manda: .webp (o formato padrão de
      // figurinha e de foto encaminhada), .heic do iPhone, .bmp, .avif. O que
      // não casava virava botão de download, e a conversa ficava com um
      // "Download" no lugar da imagem.
      return (
        <ModalImageCors
          imageUrl={message.mediaUrl}
          onAbrir={() => abrirArquivo(message.id)}
        />
      );
    } else if (message.mediaType === "audio") {
      return (
        <Audio
          url={message.mediaUrl}
          messageId={message.id}
          transcricao={message.transcription}
          transcritoEm={message.transcribedAt}
          podeTranscrever={podeTranscrever}
        />
      );
    } else if (message.mediaType === "video") {
      return (
        <div className="relative">
          <video
            className="h-[200px] w-[250px] rounded-lg object-cover"
            src={message.mediaUrl}
            controls
          />
          <Button
            variant="secondary"
            size="icon"
            className="absolute right-1 top-1 h-7 w-7 opacity-80"
            title="Abrir no visualizador"
            onClick={() => abrirArquivo(message.id)}
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      );
    } else {
      // Documento. Antes era só um botão "Download": para saber se o PDF era
      // o contrato certo, a pessoa baixava, abria fora e voltava. Agora o
      // cartão diz o que é o arquivo e abre no visualizador, sem sair daqui.
      const extensao = extensaoDe(message.mediaUrl);
      const rotulo = extensao
        ? `Arquivo ${extensao.replace(".", "").toUpperCase()}`
        : "Arquivo";

      return (
        <div className="mb-1 rounded-lg border bg-black/[0.03] p-2 dark:bg-white/[0.04]">
          {/* PDF mostra a primeira página; os outros formatos não têm como
              ser desenhados pelo navegador e ficam só no cartão. */}
          {extensao === ".pdf" && (
            <div className="mb-2">
              <MiniaturaDePdf
                url={message.mediaUrl}
                onAbrir={() => abrirArquivo(message.id)}
              />
            </div>
          )}

          <div className="flex items-center gap-2">
          <IconeDoDocumento extensao={extensao} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{rotulo}</p>
            <p className="text-xs text-muted-foreground">
              {extensao ? extensao.replace(".", "").toUpperCase() : "documento"}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            title="Visualizar"
            onClick={() => abrirArquivo(message.id)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button asChild variant="ghost" size="icon" className="h-8 w-8 shrink-0">
            <a
              href={message.mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Baixar"
            >
              <Download className="h-4 w-4" />
            </a>
          </Button>
          </div>
        </div>
      );
    }
  };

  // Tentar de novo. O sucesso não volta por aqui: o backend apaga a linha da
  // falha e emite a mensagem enviada, então a lista se corrige sozinha pelo
  // socket — inclusive para o colega que está com a mesma conversa aberta.
  const reenviarMensagem = async (message, viaReserva = false) => {
    setReenviando((atual) => ({ ...atual, [message.id]: true }));

    try {
      await api.post(`/messages/${message.id}/retry`, { viaReserva });
    } catch (err) {
      toastError(err);
    } finally {
      setReenviando((atual) => {
        const proximo = { ...atual };
        delete proximo[message.id];
        return proximo;
      });
    }
  };

  // Texto esperando a janela de junção da API Oficial (a Meta cobra por
  // mensagem). "Enviar agora" fecha a janela do atendimento inteiro: o backend
  // apaga estas bolhas e emite a mensagem juntada, então a lista se corrige
  // pelo socket.
  const renderJuncao = (message) => {
    if (message.ack !== 0 || !String(message.id || "").startsWith("juntar-")) {
      return null;
    }

    return (
      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span>Esperando para sair junto com as próximas</span>
        <Button
          variant="outline"
          size="sm"
          className="h-6 shrink-0 px-2 text-[11px]"
          disabled={!!reenviando[message.id]}
          onClick={() => enviarJuntadasAgora(message)}
        >
          {reenviando[message.id] ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Forward className="h-3 w-3" />
          )}
          Enviar agora
        </Button>
      </div>
    );
  };

  const enviarJuntadasAgora = async (message) => {
    setReenviando((atual) => ({ ...atual, [message.id]: true }));

    try {
      await api.post(`/messages/${message.ticketId}/juntadas/enviar`);
    } catch (err) {
      toastError(err);
    } finally {
      setReenviando((atual) => {
        const proximo = { ...atual };
        delete proximo[message.id];
        return proximo;
      });
    }
  };

  const renderFalhaDeEnvio = (message) => {
    if (message.ack !== ACK_FALHA) return null;

    // Falha do robô (fluxo, fila, campanha, agendamento) não ganha "Tentar de
    // novo": o fluxo já andou, e reenviar a fala solta manda para o cliente
    // uma pergunta de dois passos atrás. O motivo continua aparecendo — é o
    // que a equipe precisa saber para agir na mão.
    const automatica = String(message.id || "").startsWith("falha-auto-");

    // "ERR_SENDING_WAPP_MSG" é o genérico do backend, e não diz nada a quem
    // atende. O resto dos motivos já chega em português ("ERR_WAHA: instância
    // fora do ar"), então passa direto.
    const motivo =
      !message.sendError || message.sendError === "ERR_SENDING_WAPP_MSG"
        ? "Não foi possível enviar."
        : textoDeErro(message.sendError);

    return (
      <div className="mt-1 flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1.5">
        <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
        <span className="min-w-0 flex-1 text-[11px] leading-snug text-destructive">
          {motivo}
        </span>
        {automatica ? (
          <span className="shrink-0 text-[11px] text-destructive/80">
            Mensagem automática
          </span>
        ) : (
        <>
        <Button
          variant="outline"
          size="sm"
          className="h-6 shrink-0 px-2 text-[11px]"
          disabled={!!reenviando[message.id]}
          onClick={() => reenviarMensagem(message)}
        >
          {reenviando[message.id] ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RotateCw className="h-3 w-3" />
          )}
          Tentar de novo
        </Button>

        {/* A conexão do atendimento está fora do ar e existe uma de reserva
            no ar. Não é só reenviar: o atendimento inteiro passa para o outro
            número, senão a resposta do cliente cairia numa segunda conversa.
            Por isso o aviso no rótulo — quem clica precisa saber que o cliente
            vai receber de outro número. */}
        {reserva && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 shrink-0 px-2 text-[11px]"
            disabled={!!reenviando[message.id]}
            title={`O atendimento passa para a conexão ${reserva.name} e o cliente vai receber deste outro número.`}
            onClick={() => reenviarMensagem(message, true)}
          >
            <ArrowLeftRight className="h-3 w-3" />
            Enviar por {reserva.name}
          </Button>
        )}
        </>
        )}
      </div>
    );
  };

  const renderMessageAck = (message) => {
    // Não saiu. O tique de falha vem antes de tudo: é o único estado em que a
    // mensagem está na conversa sem ter chegado ao cliente. Vale mesmo sem
    // recibo do canal: quem grava essa falha é o nosso envio, não o provider.
    if (message.ack === ACK_FALHA) {
      return (
        <AlertCircle
          className="ml-1 inline h-3.5 w-3.5 align-middle text-destructive"
          aria-label="Não enviada"
        />
      );
    }

    // Sem recibo no canal, o único fato conhecido é "saiu daqui".
    if (!mostrarRecibos) {
      return (
        <Check className="ml-1 inline h-3.5 w-3.5 align-middle" aria-label="Enviada" />
      );
    }

    if (message.ack === 0) {
      return <Clock className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 1) {
      return <Check className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 2) {
      return <CheckCheck className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return (
        <CheckCheck className="ml-1 inline h-3.5 w-3.5 align-middle text-emerald-500" />
      );
    }
  };

  // "Hoje" e "Ontem" poupam quem lê de converter a data mentalmente; só a
  // partir de anteontem o número passa a ser mais informativo que o rótulo.
  const rotuloDoDia = (data) => {
    const dia = parseISO(data);

    if (isSameDay(dia, new Date())) return "Hoje";
    if (isSameDay(dia, subDays(new Date(), 1))) return "Ontem";

    return format(dia, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  // Já acabou o histórico? Só dá para afirmar depois de buscar a lista: antes
  // disso o botão aparece, e o clique é que descobre se há algo atrás.
  const fimDoHistorico =
    historico.tickets !== null && historico.idx >= historico.tickets.length;

  // Um clique = o próximo pedaço para trás. Enquanto o atendimento anterior
  // tiver mais páginas, o clique traz a página; esgotou, o próximo clique pula
  // para o atendimento de antes dele.
  const carregarAnterior = async () => {
    const { contactId } = dadosDoTicket;
    if (!contactId || carregandoAnterior) return;

    // Mesma marca que a paginação normal usa: o `useLayoutEffect` acima
    // devolve a rolagem quando a lista cresce por cima. Um
    // `requestAnimationFrame` próprio aqui media a altura antes de o React
    // pintar e não devolvia nada.
    marcarAlturaAtual();

    setCarregandoAnterior(true);

    try {
      let tickets = historico.tickets;

      if (tickets === null) {
        // Rota própria, e não a listagem com `contactId`: quem decide se o
        // histórico é só deste número ou da empresa inteira é o SERVIDOR, pela
        // mesma função que conta o selo "Nº atendimento" da linha. Com a tela
        // montando esse filtro, bastava a configuração mudar para a lista
        // dizer "3º atendimento" e a conversa mostrar dois.
        const { data } = await api.get(`/tickets/${ticketId}/anteriores`);

        tickets = data.anteriores || [];
      }

      const alvo = tickets[historico.idx];

      if (!alvo) {
        descartarAltura();
        setHistorico({ tickets, idx: historico.idx, pagina: 1 });
        return;
      }

      // `peek=true`: ler o atendimento antigo aqui não pode marcá-lo como
      // lido nem mexer no contador dele — quem está sendo atendido é este.
      const { data } = await api.get("/messages/" + alvo.id, {
        params: { pageNumber: historico.pagina, peek: "true" },
      });

      // Trocou de atendimento no meio do caminho: o que veio é de outra
      // conversa, e a marca de altura não pode sobrar para a próxima.
      if (currentTicketId.current !== ticketId) {
        descartarAltura();
        return;
      }

      dispatch({ type: "LOAD_MESSAGES", payload: data.messages });

      if (data.reactions) {
        dispatch({ type: "SET_REACTION", payload: { mapa: data.reactions } });
      }

      if (data.favoritedIds?.length) {
        setFavoritos((anterior) => {
          const novo = new Set(anterior);
          data.favoritedIds.forEach((id) => novo.add(id));
          return novo;
        });
      }

      setHistorico(
        data.hasMore
          ? { tickets, idx: historico.idx, pagina: historico.pagina + 1 }
          : { tickets, idx: historico.idx + 1, pagina: 1 },
      );

    } catch (err) {
      descartarAltura();
      toastError(err);
    } finally {
      setCarregandoAnterior(false);
    }
  };

  const infoDoTicket = (id) =>
    (historico.tickets || []).find((t) => String(t.id) === String(id));

  // Com histórico na tela, cada bloco precisa dizer de qual atendimento é —
  // senão a conversa de um mês atrás parece a de agora.
  const renderSeparadorDeAtendimento = (message, index) => {
    if (!misturaAtendimentos) return null;

    const anterior = index > 0 ? messagesList[index - 1] : null;
    if (anterior && String(anterior.ticketId) === String(message.ticketId)) {
      return null;
    }

    const atual = String(message.ticketId) === String(ticketId);
    const dados = infoDoTicket(message.ticketId);
    const quando = dados?.createdAt
      ? format(parseISO(dados.createdAt), "dd/MM/yyyy")
      : message.createdAt
        ? format(parseISO(message.createdAt), "dd/MM/yyyy")
        : "";

    return (
      <div
        key={`atendimento-${message.ticketId}-${message.id}`}
        className="my-3 flex w-full items-center gap-2 px-2"
      >
        <span className="h-px flex-1 bg-border" />
        <span className="flex items-center gap-1.5 rounded-full border bg-background/85 px-3 py-1 text-[11px] font-medium text-muted-foreground">
          <History className="h-3 w-3 shrink-0" />
          {atual ? "Atendimento atual" : "Atendimento encerrado"} #
          {message.ticketId}
          {quando ? ` · ${quando}` : ""}
          {!atual && dados?.queue?.name ? ` · ${dados.queue.name}` : ""}
          {!atual && dados?.user?.name ? ` · ${dados.user.name}` : ""}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
    );
  };

  const renderDailyTimestamps = (message, index) => {
    const separador = renderSeparadorDeAtendimento(message, index);
    const dia = renderDiaDaMensagem(message, index);

    if (!separador) return dia;

    return (
      <React.Fragment key={`bloco-${message.id}`}>
        {separador}
        {dia}
      </React.Fragment>
    );
  };

  const renderDiaDaMensagem = (message, index) => {
    if (index === 0) {
      return (
        <div
          key={`timestamp-${message.id}`}
          className="sticky top-1 z-10 mx-auto my-3 w-fit self-center rounded-full border bg-background/85 px-3 py-1 text-center text-[11px] font-medium text-muted-foreground shadow-xs backdrop-blur-sm"
        >
          {rotuloDoDia(messagesList[index].createdAt)}
        </div>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <div
            key={`timestamp-${message.id}`}
            className="sticky top-1 z-10 mx-auto my-3 w-fit self-center rounded-full border bg-background/85 px-3 py-1 text-center text-[11px] font-medium text-muted-foreground shadow-xs backdrop-blur-sm"
          >
            {rotuloDoDia(messagesList[index].createdAt)}
          </div>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return <span className="mt-4" key={`divider-${message.id}`}></span>;
      }
    }
  };

  // Citação cujo original NÃO está aqui: conversa anterior à conexão,
  // histórico que ninguém importou, mensagem apagada. O provider manda o
  // texto junto, e é só o que temos — por isso a caixinha não é clicável
  // (não há para onde ir) e não tem miniatura.
  const renderCitacaoOrfa = (message) => {
    return (
      <div
        className={cn(
          "-mx-1.5 -my-0.5 mb-1.5 flex overflow-hidden rounded-lg text-left",
          message.fromMe ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-muted",
        )}
        title="A mensagem citada é anterior a esta conexão e não está no histórico"
      >
        <span className="w-1 shrink-0 bg-muted-foreground/40" />
        <div className="min-w-0 flex-1 overflow-hidden whitespace-pre-wrap p-2.5 text-[0.875em]">
          <span className="flex font-medium text-muted-foreground">
            {message.quotedRemoteAuthor || "Mensagem citada"}
          </span>
          {trocarMencoes(message.quotedRemoteBody, mencoes)}
        </div>
      </div>
    );
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        role="button"
        tabIndex={0}
        // Clicar na citação leva à mensagem original — sem isso a citação diz
        // "sobre isto aqui" e deixa a pessoa rolando a conversa à mão.
        title="Ir para a mensagem citada"
        onClick={() => irParaMensagem(message.quotedMsg?.id)}
        onKeyDown={(e) =>
          e.key === "Enter" && irParaMensagem(message.quotedMsg?.id)
        }
        // O duplo clique aqui dentro não deve virar "responder a citação".
        onDoubleClick={(e) => e.stopPropagation()}
        className={cn(
          "-mx-1.5 -my-0.5 mb-1.5 flex cursor-pointer overflow-hidden rounded-lg text-left hover:brightness-95",
          message.fromMe ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-muted",
        )}
      >
        <span
          className={cn(
            "w-1 shrink-0",
            message.quotedMsg?.fromMe ? "bg-emerald-400" : "bg-sky-400",
          )}
        />
        <div className="min-w-0 flex-1 overflow-hidden whitespace-pre-wrap p-2.5 text-[0.875em]">
          {!message.quotedMsg?.fromMe && (
            <span className="flex font-medium text-sky-500">
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {/* Mídia sem legenda tem corpo vazio: sem o rótulo a citação era
              um retângulo em branco e ninguém sabia o que foi respondido. */}
          {trocarMencoes(message.quotedMsg?.body, mencoes) ||
            rotuloDeMidia(message.quotedMsg?.mediaType)}
        </div>
        {temMiniatura(message.quotedMsg) &&
          (message.quotedMsg.mediaType === "video" ? (
            <video
              className="h-14 w-14 shrink-0 object-cover"
              src={message.quotedMsg.mediaUrl}
              preload="metadata"
              muted
            />
          ) : (
            <img
              className="h-14 w-14 shrink-0 object-cover"
              src={message.quotedMsg.mediaUrl}
              alt=""
            />
          ))}
      </div>
    );
  };

  // O canto reto de um lado só é o que dá a "ponta" da bolha e diz de quem
  // é a fala sem precisar de cor forte. Sombra fica quase imperceptível: com
  // centenas de bolhas na tela, sombra pesada vira ruído.
  const bubbleBase =
    "relative mt-0.5 block h-auto max-w-[85%] whitespace-pre-wrap wrap-break-word rounded-2xl px-3 pb-1.5 pt-2 shadow-[0_1px_2px_rgba(0,0,0,0.06)] sm:max-w-[600px]";

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        // Nota da equipe: centralizada e sem bolha, para ninguém confundir
        // com algo que o cliente recebeu.
        if (message.isInternal) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              <div className="my-2 flex w-full justify-center px-4">
                <span className="flex items-center gap-1.5 rounded-full border border-dashed bg-muted/60 px-3 py-1 text-center text-xs text-muted-foreground">
                  <StickyNote className="h-3 w-3 shrink-0" />
                  {message.body}
                </span>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                id={`mensagem-${message.id}`}
                onDoubleClick={() => responderMensagem(message)}
                title="Dois cliques para responder"
                className={cn(
                  bubbleBase,
                  "group mr-5 self-start rounded-bl-md bg-white text-slate-800 dark:bg-slate-800 dark:text-slate-100",
                  destacada === message.id &&
                    "ring-2 ring-amber-400 ring-offset-1",
                  classesDeSelecao(message),
                )}
              >
                {renderCapaDeSelecao(message)}
                <button
                  type="button"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  className="absolute right-0 top-0 hidden h-7 w-7 items-center justify-center rounded-full bg-inherit text-muted-foreground opacity-90 group-hover:flex hover:bg-black/5"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                {renderFavoritoBotao(message)}
                {isGroup && (
                  <span className="flex font-medium text-sky-500">
                    {message.contact?.name}
                  </span>
                )}
                {(message.mediaUrl ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard") &&
                  checkMessageMedia(message)}
                <div
                  className={cn(
                    "wrap-break-word pb-1 pt-0.5",
                    // O rodapé é absoluto no canto: o texto precisa reservar a
                    // largura dele, senão a última linha some por baixo. Com
                    // "editada" ao lado da hora o rodapé é bem mais largo.
                    espacoDoRodape(message),
                  )}
                >
                  {message.quotedMsg
                    ? renderQuotedMessage(message)
                    : message.quotedRemoteBody &&
                      renderCitacaoOrfa(message)}
                  {message.templateData ? (
                    <TemplateMessage dados={message.templateData} />
                  ) : (
                    <MarkdownWrapper>
                      {trocarMencoes(corpoVisivel(message), mencoes)}
                    </MarkdownWrapper>
                  )}
                  {renderReacoes(message)}
                  <span className="absolute bottom-0.5 right-1.5 flex items-center gap-1 text-[0.6875em] text-muted-foreground">
                    {renderMarcaDeEdicao(message)}
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                // Âncora da busca: é por este id que o resultado escolhido
                // encontra o balão para rolar até ele.
                id={`mensagem-${message.id}`}
                onDoubleClick={() => responderMensagem(message)}
                title="Dois cliques para responder"
                className={cn(
                  bubbleBase,
                  "group ml-5 self-end rounded-br-md bg-[#dcf8c6] text-slate-800 dark:bg-emerald-900/45 dark:text-slate-100",
                  destacada === message.id &&
                    "ring-2 ring-amber-400 ring-offset-1",
                  classesDeSelecao(message),
                )}
              >
                {renderCapaDeSelecao(message)}
                <button
                  type="button"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  className="absolute right-0 top-0 hidden h-7 w-7 items-center justify-center rounded-full bg-inherit text-muted-foreground opacity-90 group-hover:flex hover:bg-black/5"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                {renderFavoritoBotao(message)}
                {(message.mediaUrl ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard") &&
                  checkMessageMedia(message)}
                <div
                  className={cn(
                    "wrap-break-word pb-1 pt-0.5",
                    espacoDoRodape(message),
                    message.isDeleted && "italic text-muted-foreground",
                  )}
                >
                  {message.isDeleted && (
                    <Ban className="mr-1 inline h-3.5 w-3.5 align-middle" />
                  )}
                  {message.quotedMsg
                    ? renderQuotedMessage(message)
                    : message.quotedRemoteBody &&
                      renderCitacaoOrfa(message)}
                  {message.templateData ? (
                    <TemplateMessage dados={message.templateData} />
                  ) : (
                    <MarkdownWrapper>
                      {trocarMencoes(corpoVisivel(message), mencoes)}
                    </MarkdownWrapper>
                  )}
                  {renderReacoes(message)}
                  {renderFalhaDeEnvio(message)}
                  {renderJuncao(message)}
                  <span className="absolute bottom-0.5 right-1.5 flex items-center gap-1 text-[0.6875em] text-muted-foreground">
                    {renderMarcaDeEdicao(message)}
                    {renderOrigemDoEnvio(message)}
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else if (loading) {
      // Ainda buscando: nada na tela. Sem isto, o texto de conversa nova
      // piscava em toda conversa antes das mensagens chegarem.
      return [];
    } else if (bloqueadoAteAceitar) {
      return (
        <div className="m-auto flex max-w-sm flex-col items-center gap-2 rounded-lg border border-dashed bg-background/70 px-4 py-4 text-center text-sm text-muted-foreground">
          <Lock className="h-5 w-5" />
          <span className="font-medium text-foreground">
            A conversa aparece depois de aceitar
          </span>
          Seu cargo não permite ler o que foi dito antes de assumir este
          atendimento. Quem está esperando, o departamento, o canal e o tempo de espera
          continuam à vista no cabeçalho.
        </div>
      );
    } else if (erroAoCarregar) {
      // A busca falhou: a conversa pode estar cheia do outro lado. Dizer
      // "comece a conversa" aqui é mentira — e apaga o histórico aos olhos de
      // quem atende.
      return (
        <div className="m-auto flex flex-col items-center gap-2 rounded-lg border border-dashed bg-background/70 px-4 py-3 text-center text-sm text-muted-foreground">
          Não foi possível carregar as mensagens desta conversa.
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTentativa((n) => n + 1)}
          >
            <RotateCw className="h-3 w-3" />
            Tentar de novo
          </Button>
        </div>
      );
    } else if (filtrando) {
      // Sem isto, filtrar e não achar nada mostrava "Say hello to your new
      // contact!" — quem lia achava que a conversa tinha sumido. Vem depois de
      // carregando e de erro: o filtro só pode dizer "nada encontrado" quando
      // a busca terminou, e terminou bem.
      return (
        <div className="m-auto flex flex-col items-center gap-2 rounded-lg border border-dashed bg-background/70 px-4 py-3 text-center text-sm text-muted-foreground">
          {apenasFavoritas
            ? "Nenhuma mensagem favorita nesta conversa."
            : "Nenhuma mensagem no período escolhido."}
          <Button variant="outline" size="sm" onClick={limparFiltros}>
            Limpar filtro
          </Button>
        </div>
      );
    } else {
      return (
        <div className="m-auto rounded-lg border border-dashed bg-background/70 px-4 py-3 text-center text-sm text-muted-foreground">
          Comece a conversa com este contato.
        </div>
      );
    }
  };

  const limparFiltros = () => {
    setDataInicio("");
    setDataFim("");
    setApenasFavoritas(false);
  };

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      <MessageOptionsMenu
        message={selectedMessage}
        podeReagir={podeReagir}
        podeEditar={podeEditar && editavelAgora}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      {/* Barra de filtros. Recolhida por padrão: na maior parte do tempo o que
          se quer é a conversa inteira, e três campos fixos aqui roubariam
          altura da leitura no celular. */}
      <div className="flex flex-none flex-wrap items-center gap-2 border-b bg-background px-3 py-1.5">
        {temBotao("filtrar") && (
          <Button
            variant={filtrosAbertos || filtrando ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setFiltrosAbertos((aberto) => !aberto)}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filtrar
          </Button>
        )}

        {temBotao("selecionar") && (
          <Button
            variant={modoSelecao ? "secondary" : "ghost"}
            size="sm"
            onClick={() =>
              modoSelecao ? sairDaSelecao() : setModoSelecao(true)
            }
            title="Escolher arquivos da conversa para baixar"
          >
            <CheckSquare className="h-4 w-4" />
            Selecionar
          </Button>
        )}

        {temBotao("favoritas") && (
          <Button
            variant={apenasFavoritas ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setApenasFavoritas((ativo) => !ativo)}
            title="Mostrar só as mensagens que você favoritou"
          >
            <Star
              className={cn(
                "h-4 w-4",
                apenasFavoritas && "fill-amber-400 text-amber-500",
              )}
            />
            Favoritas
          </Button>
        )}

        {filtrando && (
          <Button variant="ghost" size="sm" onClick={limparFiltros}>
            <X className="h-4 w-4" />
            Limpar
          </Button>
        )}

        {/* A busca fica na mesma barra, mas não recorta a conversa: ela só
            aponta para onde ir, e o contador diz quantas vezes o termo
            aparece — como no WhatsApp. */}
        <div
          className={`ml-auto flex items-center gap-1 ${
            // Aberta por um resultado de busca, aparece mesmo com o botão
            // escondido: o "1 de 5" é o que diz onde a pessoa está.
            temBotao("buscar") || termoBusca ? "" : "hidden"
          }`}
        >
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={buscaRef}
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              onKeyDown={aoTeclarBusca}
              placeholder="Buscar na conversa"
              aria-label="Buscar na conversa"
              className="h-8 w-40 pl-7 text-xs sm:w-52"
            />
          </div>

          {buscando && (
            <span className="text-xs text-muted-foreground">…</span>
          )}

          {!buscando && termoBusca.trim().length >= 2 && (
            <>
              <span
                aria-live="polite"
                className="whitespace-nowrap text-xs text-muted-foreground"
              >
                {achados.length
                  ? `${indiceAchado + 1} de ${achados.length}`
                  : "Nenhuma ocorrência"}
              </span>

              {achados.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Resultado anterior (mais recente)"
                    onClick={() =>
                      irParaAchado(
                        (indiceAchado - 1 + achados.length) % achados.length,
                      )
                    }
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Próximo resultado (mais antigo)"
                    onClick={() =>
                      irParaAchado((indiceAchado + 1) % achados.length)
                    }
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </>
              )}

              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title="Limpar a busca"
                onClick={limparBusca}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>

        {filtrosAbertos && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              De
              <Input
                type="date"
                value={dataInicio}
                max={dataFim || undefined}
                onChange={(e) => setDataInicio(e.target.value)}
                className="h-8 w-[9.5rem]"
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Até
              <Input
                type="date"
                value={dataFim}
                min={dataInicio || undefined}
                onChange={(e) => setDataFim(e.target.value)}
                className="h-8 w-[9.5rem]"
              />
            </label>
          </div>
        )}
      </div>

      {/* Barra do modo seleção. Fica presa acima da conversa: rolando atrás
          dela, o que foi marcado e o botão de baixar continuam à mão. */}
      <ForwardMessageModal
        open={encaminhandoSelecao}
        // Só fecha a janela: a seleção fica de pé. Cancelar por engano e
        // perder as doze mensagens marcadas seria trabalho jogado fora.
        onClose={() => setEncaminhandoSelecao(false)}
        onEnviado={sairDaSelecao}
        messages={mensagensSelecionadas}
      />

      {modoSelecao && (
        <div className="flex flex-none flex-wrap items-center gap-2 border-b bg-primary/5 px-3 py-2">
          <span className="text-sm font-medium">
            {selecionadas.size} selecionada{selecionadas.size === 1 ? "" : "s"}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={alternarTodosDaConversa}
            disabled={!arquivosDaConversa.length}
          >
            {todosArquivosMarcados ? "Desmarcar todos" : "Marcar todos à vista"}
          </Button>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => baixarArquivos(arquivosSelecionados, "selecionados")}
              disabled={!arquivosSelecionados.length || !!baixando}
            >
              {baixando === "selecionados" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Baixar arquivos ({arquivosSelecionados.length})
            </Button>

            {/* Encaminhar vale para QUALQUER mensagem marcada, com ou sem
                anexo — é o outro uso do mesmo modo de seleção. */}
            <Button
              size="sm"
              onClick={() => setEncaminhandoSelecao(true)}
              disabled={!selecionadas.size}
            >
              <Forward className="h-4 w-4" />
              Encaminhar ({selecionadas.size})
            </Button>

            {/* Lista vazia = tudo o que o atendimento tem no banco, e não só o
                que já foi rolado na tela. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => baixarArquivos([], "tudo")}
              disabled={!!baixando}
            >
              {baixando === "tudo" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Baixar tudo da conversa
            </Button>

            <Button variant="ghost" size="sm" onClick={sairDaSelecao}>
              <X className="h-4 w-4" />
              Cancelar
            </Button>
          </div>

          <p className="w-full text-xs text-muted-foreground">
            Toque nas mensagens com anexo para marcar. Imagens, vídeos, áudios,
            PDFs e documentos entram num ZIP separado por tipo.
          </p>
        </div>
      )}

      <VisualizadorDeArquivo
        open={visualizando !== null}
        onClose={() => setVisualizando(null)}
        arquivos={arquivosDaConversa}
        indice={visualizando ?? 0}
        onIndice={setVisualizando}
      />

      {/* A textura é clara; no tema escuro ela entra esmaecida sobre o fundo
          do tema para as bolhas não perderem contraste. */}
      {/* O tamanho do texto da conversa vive numa variável neste contêiner, e
          o que escala com ela usa `em` — não `rem`, que ignora o contêiner e
          olha só o <html>. É o que deixa ícone, selo e botão do tamanho de
          sempre enquanto só a conversa cresce. */}
      <div
        id="messagesList"
        onScroll={handleScroll}
        style={{ "--tamanho-msg": TAMANHOS_DA_FONTE[tamanhoFonteChat] }}
        className="scrollbar-discreta relative flex flex-1 flex-col overflow-y-auto bg-muted/40 text-[length:var(--tamanho-msg)]"
      >
        {/* Este wrapper existe para a textura ter em que se ancorar.
            Ela era `absolute inset-0` direto no contêiner de rolagem, e
            elemento absoluto ali é dimensionado pela área VISÍVEL, não pela
            altura do conteúdo: passando de uma tela de mensagens, o resto
            rolava para fora da textura e o fundo ficava branco.
            Aqui a altura acompanha as mensagens, e o min-h-full garante a
            textura inteira também quando há pouca conversa. O shrink-0 é
            obrigatório: o pai é flex-col, e item flex encolhe para caber em
            vez de crescer — sem ele o wrapper voltava a ter a altura da
            janela e o problema continuava. */}
        <div className="relative flex min-h-full shrink-0 flex-col p-3 pb-24 sm:p-5 sm:pb-5">
          {texturaDoChat && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-repeat opacity-100 dark:opacity-[0.06]"
              style={{ backgroundImage: `url(${whatsBackground})` }}
            />
          )}
          <div className="relative flex flex-1 flex-col">
            {/* Atendimento anterior deste mesmo contato. Só depois que o
                atendimento atual chegou ao topo: oferecer o histórico antes
                disso é oferecer o passado a quem ainda não viu o presente. */}
            {!filtrando &&
              !hasMore &&
              !loading &&
              !bloqueadoAteAceitar &&
              !fimDoHistorico &&
              dadosDoTicket.contactId && (
                <div className="mb-2 flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={carregandoAnterior}
                    onClick={carregarAnterior}
                    className="gap-1.5 bg-background/85 text-xs"
                  >
                    {carregandoAnterior ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <History className="h-3 w-3" />
                    )}
                    Carregar atendimento anterior
                  </Button>
                </div>
              )}

            {/* Clicou e não havia nada: o botão sumir sozinho parece falha.
                Sem requisição extra — é a mesma resposta que acabou de vir. */}
            {historico.tickets !== null &&
              !historico.tickets.length &&
              !misturaAtendimentos &&
              !filtrando &&
              !loading && (
                <p className="mb-2 text-center text-xs text-muted-foreground">
                  Nenhum atendimento anterior deste contato.
                </p>
              )}
            {/* Sempre chamado: era a condição daqui que engolia os estados
                vazios — com a lista vazia e sem filtro, a função nem rodava e
                o texto de conversa nova nunca aparecia. Quem decide o que
                mostrar (mensagens, filtro sem resultado, conversa nova ou
                nada enquanto carrega) é o próprio `renderMessages`. */}
            {renderMessages()}
          </div>
        </div>
      </div>
      {foraDoFim && (
        <Button
          size="icon"
          variant="secondary"
          aria-label="Ir para a última mensagem"
          title="Ir para a última mensagem"
          onClick={() => scrollToBottom("smooth")}
          className="absolute bottom-4 right-4 z-10 rounded-full border bg-background shadow-lg hover:bg-accent"
        >
          <ChevronDown className="h-5 w-5" />
        </Button>
      )}
      {loading && (
        <div className="absolute left-1/2 top-3 -translate-x-1/2">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-500 opacity-70" />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
